/**
 * Importazione dati — redesign Feb 2026 v34.
 *
 * UX semplificata: gruppi, terminologia italiana, wizard 5-step, storico
 * collassabile, "Vedi esempio compilato" per ogni entità. Il motore backend
 * (dry-run/apply/idempotenza/audit/report) è invariato.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Section, Card, Badge, Button, Alert, Input } from "../design-system";
import { useAuth } from "../auth/AuthProvider";

// ---------- Metadati UI per ogni entità (label leggibile + gruppo + esempio) ----------
// Feb 2026 v34 — descrizioni e obbligatorietà derivate dagli importer reali
// (services/importer/*.py). Se il backend aggiunge campi non elencati qui,
// il template scaricato resta corretto (backend è la fonte di verità).
const KIND_META = {
  // ---- Anagrafiche
  customers: { label: "Clienti", group: "Anagrafiche",
    desc: "Aggiorna o crea l'anagrafica clienti (matching per codice).",
    required: ["code", "business_name"],
    example: [
      { code: "0001048", business_name: "SPEEDYFOOD SAS", vat_number: "01234567890", email: "info@speedyfood.it", phone: "0836/550524", category_code: "HORECA", agent_code: "10", region: "Puglia", status: "ACTIVE", notes: "" },
      { code: "0002030", business_name: "PIZZERIA DEMO SRL", vat_number: "02345678901", email: "amm@pizzademo.it", phone: "0575/000111", category_code: "HORECA", agent_code: "10", region: "Toscana", status: "ACTIVE", notes: "" },
    ]},
  zucchetti_products: { label: "Prodotti (Zucchetti)", group: "Avanzati",
    desc: "Import prodotti dal gestionale Zucchetti (formato specifico).",
    required: ["sku"], example: [{ sku: "1001", name: "HAMB. ANGUS IRLANDESE 200g", kg_per_carton: "4.8" }]},
  zucchetti_agents: { label: "Agenti (Zucchetti)", group: "Avanzati",
    desc: "Import agenti dal gestionale Zucchetti.",
    required: ["code"], example: [{ code: "10", first_name: "", last_name: "", display_name: "G.E." }]},
  zucchetti_customers: { label: "Clienti (Zucchetti)", group: "Avanzati",
    desc: "Import clienti dal gestionale Zucchetti (matching per codice).",
    required: ["code", "business_name"], example: [{ code: "0001048", business_name: "SPEEDYFOOD SAS" }]},
  carriers: { label: "Vettori", group: "Anagrafiche",
    desc: "Anagrafica vettori/corrieri.",
    required: ["code", "business_name"], example: [
      { code: "36", business_name: "ATLANTICA TRASPORTI SRL", phone: "0810000000", email: "info@atlantica.it", admin_email: "billing@atlantica.it", notes: "", is_active: "true" },
      { code: "40", business_name: "STEF Italia Spa", phone: "", email: "", admin_email: "", notes: "", is_active: "true" },
    ]},
  // ---- Commerciale
  price_lists: { label: "Listini prezzi", group: "Commerciale",
    desc: "Crea/aggiorna un listino (metadata). Le righe SKU si importano in 'Prezzi listino'.",
    required: ["code", "name", "valid_from"],
    example: [
      { code: "PL-HORECA", name: "Listino Horeca 2026", category_code: "HORECA", valid_from: "2026-01-01", valid_to: "", is_active: "true", is_default: "false" },
      { code: "PL-GDO", name: "Listino GDO 2026", category_code: "GDO", valid_from: "2026-01-01", valid_to: "", is_active: "true", is_default: "false" },
      { code: "PL-DEFAULT", name: "Listino Default 2026", category_code: "", valid_from: "2026-01-01", valid_to: "", is_active: "true", is_default: "true" },
    ]},
  price_list_items: { label: "Prezzi listino", group: "Commerciale",
    desc: "Popola le righe SKU + prezzo di un listino esistente (matching per price_list_code).",
    required: ["price_list_code", "sku", "unit_price"],
    example: [
      { price_list_code: "PL-HORECA", sku: "749", unit_price: "4.25", min_qty: "", notes: "" },
      { price_list_code: "PL-HORECA", sku: "193", unit_price: "5.10", min_qty: "1", notes: "" },
      { price_list_code: "PL-HORECA", sku: "197A", unit_price: "3.95", min_qty: "", notes: "solo forni" },
    ]},
  contracts: { label: "Contratti", group: "Commerciale",
    desc: "Contratti quadro cliente/cooperativa con termini di pagamento.",
    required: ["code", "name", "owner_type"],
    example: [{ code: "C-2026-001", name: "Contratto SPEEDYFOOD 2026", owner_type: "CUSTOMER", customer_code: "0001048", starts_at: "2026-01-01", ends_at: "2026-12-31", is_active: "true", payment_method: "BONIFICO", payment_days: "30", advance_payment_allowed: "true", advance_discount_percent: "1.0", pallet_mono_discount_percent: "2.0" }]},
  cooperatives: { label: "Cooperative", group: "Commerciale",
    desc: "Anagrafica cooperative di acquisto.",
    required: ["code", "name"], example: [{ code: "COOP-01", name: "Coop Ristoratori Puglia", is_active: "true" }]},
  cooperative_memberships: { label: "Soci cooperative", group: "Commerciale",
    desc: "Associazione cliente ↔ cooperativa con validità.",
    required: ["cooperative_code", "customer_code"],
    example: [{ cooperative_code: "COOP-01", customer_code: "0001048", valid_from: "2026-01-01", valid_to: "", is_active: "true" }]},
  // ---- Logistica
  delivery_points: { label: "Punti di consegna", group: "Logistica",
    desc: "Sedi di consegna per cliente (external_ref = codice Zucchetti).",
    required: ["customer_code", "name"],
    example: [
      { customer_code: "0001048", external_ref: "01", name: "SEDE", address: "VIA ROMA 10", zip_code: "73100", city: "LECCE", province: "LE", is_default: "true" },
      { customer_code: "0001048", external_ref: "02", name: "SECONDARIA", address: "VIA VERDI 5", zip_code: "73100", city: "LECCE", province: "LE", is_default: "false" },
    ]},
  logistics_pallet: { label: "Palletizzazione", group: "Avanzati",
    desc: "Profili di palletizzazione (cartons_per_layer, layers_per_pallet).",
    required: ["sku"], example: [{ sku: "1001", cartons_per_layer: "9", layers_per_pallet: "6" }]},
};

const GROUPS = ["Anagrafiche", "Commerciale", "Logistica", "Avanzati"];
const STATUS_LABEL = { RUNNING: "In corso", PENDING: "In attesa", SUCCEEDED: "Completato", COMPLETED: "Completato", FAILED: "Errore", CANCELLED: "Annullato" };

export default function AdminImportCenterPage() {
  const { apiGet, token } = useAuth();
  const [kinds, setKinds] = useState([]);
  const [env, setEnv] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedKind, setSelectedKind] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // {batch_id, counts:{created,updated,skipped,errors,rows_read}}
  const [applied, setApplied] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorRows, setErrorRows] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [k, e, b] = await Promise.all([
        apiGet("/api/admin/imports/kinds"),
        apiGet("/api/admin/imports/environment"),
        apiGet("/api/admin/imports?limit=50"),
      ]);
      setKinds(k || []); setEnv(e); setBatches(b || []);
      if (!selectedKind && k?.length) setSelectedKind(k[0].kind);
    } catch (ex) { setErr(ex?.message || String(ex)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const grouped = useMemo(() => {
    const map = { Anagrafiche: [], Commerciale: [], Logistica: [], Avanzati: [] };
    for (const k of kinds) {
      const m = KIND_META[k.kind] || { label: k.kind, group: "Avanzati" };
      map[m.group].push({ ...k, ...m });
    }
    return map;
  }, [kinds]);

  const currentMeta = selectedKind ? (KIND_META[selectedKind] || { label: selectedKind, group: "Avanzati" }) : null;
  const currentKindObj = kinds.find(k => k.kind === selectedKind);
  const currentHeaders = currentKindObj?.headers || [];

  const resetFlow = () => { setFile(null); setPreview(null); setApplied(null); setErrorRows([]); setShowErrors(false); };
  const chooseKind = (kind) => { setSelectedKind(kind); resetFlow(); };

  const downloadTemplate = async () => {
    if (!selectedKind) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/imports/templates/${selectedKind}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { alert("Errore download modello"); return; }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${selectedKind}_modello.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const downloadExample = () => {
    if (!currentMeta?.example) return;
    const rows = currentMeta.example;
    const headers = currentHeaders.length ? currentHeaders : Object.keys(rows[0]);
    const csv = [headers.join(",")].concat(
      rows.map(r => headers.map(h => JSON.stringify(r[h] ?? "").replace(/^"|"$/g, "")).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${selectedKind}_esempio.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const doCheck = async () => {
    if (!file || !selectedKind) return;
    setBusy(true); setErr(null); setApplied(null);
    try {
      const fd = new FormData();
      fd.append("kind", selectedKind); fd.append("file", file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/imports/preview`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Errore controllo file");
      setPreview(data);
    } catch (ex) { setErr(ex.message || String(ex)); }
    finally { setBusy(false); }
  };

  const doApply = async () => {
    if (!preview) return;
    setBusy(true); setErr(null); setShowConfirm(false);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/imports/apply`,
        { method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ batch_id: preview.batch_id }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Errore importazione");
      setApplied(data);
      await reload();
    } catch (ex) { setErr(ex.message || String(ex)); }
    finally { setBusy(false); }
  };

  const loadErrors = async () => {
    if (!preview?.batch_id) return;
    try {
      const rows = await apiGet(`/api/admin/imports/${preview.batch_id}/rows?status=ERROR&limit=100`);
      setErrorRows(rows || []); setShowErrors(true);
    } catch (ex) { setErr(ex.message || String(ex)); }
  };

  const counts = preview?.counts || applied?.counts || null;
  const hasErrors = counts && (counts.errors || 0) > 0;
  const canImport = preview && !hasErrors && !applied;

  return (
    <Section title="Importazione dati"
             subtitle="Carica e aggiorna i dati del portale da file CSV/XLSX"
             data-testid="admin-import-page">
      {err && <Alert variant="danger" data-testid="import-error">{err}</Alert>}
      {env && (
        <div style={{ padding: 10, borderRadius: 6, marginBottom: 12,
                      background: env.environment === "PRODUCTION" ? "#FEF3C7" : "#DBEAFE",
                      border: `1px solid ${env.environment === "PRODUCTION" ? "#F59E0B" : "#3B82F6"}` }}
             data-testid="import-env-banner">
          <strong>{env.environment === "PRODUCTION" ? "Ambiente di produzione" : "Ambiente di test"}</strong>
          {env.environment !== "PRODUCTION" && " — nessuna modifica ai dati di produzione"}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16 }}>
        {/* Sidebar entità raggruppate */}
        <Card><Card.Body style={{ padding: 6 }}>
          {GROUPS.map(g => {
            const items = grouped[g] || [];
            if (!items.length) return null;
            const isAdvanced = g === "Avanzati";
            return (
              <details key={g} open={!isAdvanced} style={{ marginBottom: 6 }}>
                <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: ".82rem",
                                  color: "#0F4C81", padding: "4px 8px" }}>
                  {isAdvanced ? "Import avanzati" : g}
                </summary>
                {items.map(k => (
                  <div key={k.kind} onClick={() => chooseKind(k.kind)}
                       data-testid={`import-tab-${k.kind}`}
                       style={{ padding: "6px 12px", cursor: "pointer", fontSize: ".85rem",
                                borderRadius: 4, marginTop: 2,
                                background: selectedKind === k.kind ? "#DBEAFE" : "transparent",
                                fontWeight: selectedKind === k.kind ? 600 : 400 }}>
                    {k.label}
                  </div>
                ))}
              </details>
            );
          })}
        </Card.Body></Card>
        {/* Wizard principale */}
        {currentMeta && (
          <Card data-testid="import-wizard">
            <Card.Header><strong>Importa {currentMeta.label}</strong></Card.Header>
            <Card.Body>
              <p className="muted" style={{ marginTop: 0, fontSize: ".88rem" }}>{currentMeta.desc}</p>
              <ol style={{ paddingLeft: 20, fontSize: ".82rem", color: "#64748b" }}>
                <li>Scarica modello vuoto o vedi esempio compilato</li>
                <li>Scegli il file CSV/XLSX</li>
                <li>Controlla file (verifica righe/errori)</li>
                <li>Importa (dopo conferma)</li>
              </ol>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                <Button onClick={downloadTemplate} data-testid="import-download-template">Scarica modello</Button>
                <Button onClick={() => setShowExample(true)} data-testid="import-view-example">Vedi esempio compilato</Button>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Input type="file" accept=".csv,.xlsx" data-testid="import-file-input"
                       onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setApplied(null); }} />
                {file && <div className="muted" style={{ fontSize: ".78rem", marginTop: 4 }}>Selezionato: {file.name}</div>}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="primary" onClick={doCheck} disabled={!file || busy}
                        data-testid="import-check-btn">
                  {busy && !preview ? "Controllo…" : "Controlla file"}
                </Button>
                <Button variant="primary" onClick={() => setShowConfirm(true)} disabled={!canImport || busy}
                        data-testid="import-apply-btn">Importa</Button>
              </div>
              {counts && !applied && (
                <div style={{ marginTop: 12, padding: 10, background: "#F8FAFC", borderRadius: 6, border: "1px solid #E2E8F0" }}
                     data-testid="import-preview-summary">
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>File controllato</div>
                  <div style={{ fontSize: ".85rem" }}>
                    <span data-testid="import-ready-count">
                      Righe pronte all'importazione: <strong>{Math.max(0, (counts.rows_read || counts.total || 0) - (counts.errors || counts.errored || 0) - (counts.skipped || 0))}</strong>
                    </span>
                    {" · "}{counts.updated || 0} da aggiornare · {counts.created || 0} nuove · {counts.skipped || 0} ignorate ·{" "}
                    <span style={{ color: hasErrors ? "#DC2626" : "#16A34A", fontWeight: 600 }}>
                      {counts.errors || counts.errored || 0} errori
                    </span>
                  </div>
                  {hasErrors && (
                    <Button size="sm" onClick={loadErrors} data-testid="import-view-errors"
                            style={{ marginTop: 6 }}>Vedi errori</Button>
                  )}
                  {/* v34.1 — mini-preview delle prime 5 righe parsate */}
                  {preview?.parsed_preview?.length > 0 && (() => {
                    const cols = (currentMeta?.required || []).concat(
                      currentHeaders.filter(h => !(currentMeta?.required || []).includes(h))
                    ).slice(0, 3);
                    return (
                      <div style={{ marginTop: 10 }} data-testid="import-parsed-preview">
                        <div style={{ fontWeight: 600, fontSize: ".82rem", marginBottom: 4 }}>
                          Anteprima prime {preview.parsed_preview.length} righe
                        </div>
                        <div className="ds-table-wrap">
                          <table className="ds-table" style={{ fontSize: ".78rem" }}>
                            <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
                            <tbody>
                              {preview.parsed_preview.map((row, i) => (
                                <tr key={i} data-testid={`import-preview-row-${i}`}>
                                  {cols.map(c => <td key={c}>{row[c] ?? ""}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                  {(!preview?.parsed_preview || preview.parsed_preview.length === 0) &&
                    preview?.preview_rows?.length > 0 && (
                    <div style={{ marginTop: 10 }} data-testid="import-fallback-preview">
                      <div style={{ fontWeight: 600, fontSize: ".82rem", marginBottom: 4 }}>
                        Anteprima esito prime 5 righe
                      </div>
                      <div className="ds-table-wrap">
                        <table className="ds-table" style={{ fontSize: ".78rem" }}>
                          <thead><tr><th>Riga</th><th>Chiave</th><th>Stato</th></tr></thead>
                          <tbody>
                            {preview.preview_rows.slice(0, 5).map((r, i) => (
                              <tr key={i}>
                                <td>{r.row}</td>
                                <td>{r.business_key || "—"}</td>
                                <td>{r.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {applied && (
                <div style={{ marginTop: 12, padding: 10, background: "#DCFCE7", borderRadius: 6, border: "1px solid #16A34A" }}
                     data-testid="import-applied-summary">
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Importazione completata</div>
                  <div style={{ fontSize: ".85rem" }}>
                    Creati: {applied.counts?.created || 0} · Aggiornati: {applied.counts?.updated || 0} · Ignorati: {applied.counts?.skipped || 0} · Errori: {applied.counts?.errors || 0}
                  </div>
                  <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/imports/${applied.batch_id}/report`}
                       target="_blank" rel="noopener noreferrer">
                      <Button size="sm">Scarica report</Button>
                    </a>
                    <Button size="sm" onClick={resetFlow}>Nuova importazione</Button>
                  </div>
                </div>
              )}
              {showErrors && (
                <div style={{ marginTop: 12, padding: 10, background: "#FEF2F2", borderRadius: 6, border: "1px solid #DC2626" }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Righe con errori</div>
                  <div style={{ maxHeight: 240, overflow: "auto", fontSize: ".78rem" }}>
                    {errorRows.slice(0, 50).map((r, i) => (
                      <div key={i} style={{ padding: "3px 0", borderBottom: "1px solid #FEE2E2" }}>
                        Riga {r.row_number || "?"}: {r.error_message || r.error || "errore"}
                      </div>
                    ))}
                  </div>
                  <Button size="sm" onClick={() => setShowErrors(false)} style={{ marginTop: 6 }}>Chiudi</Button>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </div>
      {/* Storico collassabile */}
      <details style={{ marginTop: 16 }} open={showHistory} onToggle={e => setShowHistory(e.target.open)}
               data-testid="import-history-accordion">
        <summary style={{ cursor: "pointer", fontWeight: 700, padding: 6 }}>
          Storico importazioni ({batches.length})
        </summary>
        <Card style={{ marginTop: 6 }}>
          <Card.Body style={{ padding: 0 }}>
            <div className="ds-table-wrap">
              <table className="ds-table">
                <thead><tr><th>Data</th><th>Tipo</th><th>Ambiente</th><th>Stato</th><th>Creati</th><th>Aggiornati</th><th>Ignorati</th><th>Errori</th><th>Report</th></tr></thead>
                <tbody>
                  {batches.slice(0, 50).map(b => (
                    <tr key={b.id} data-testid={`history-row-${b.id}`}>
                      <td>{b.created_at?.split("T")[0]}</td>
                      <td>{KIND_META[b.kind]?.label || b.kind}</td>
                      <td>{b.environment}</td>
                      <td><Badge tone={b.status === "SUCCEEDED" || b.status === "COMPLETED" ? "success" : b.status === "FAILED" ? "danger" : "info"}>{STATUS_LABEL[b.status] || b.status}</Badge></td>
                      <td>{b.counts?.created || 0}</td>
                      <td>{b.counts?.updated || 0}</td>
                      <td>{b.counts?.skipped || 0}</td>
                      <td>{b.counts?.errors || 0}</td>
                      <td><a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/imports/${b.id}/report`} target="_blank" rel="noopener noreferrer">Report</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </details>
      {/* Modal esempio compilato */}
      {showExample && currentMeta && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", display: "flex",
                      alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
             onClick={() => setShowExample(false)}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: "#fff", borderRadius: 8, padding: 20, maxWidth: 900, width: "100%",
                        maxHeight: "80vh", overflow: "auto" }}
               data-testid="import-example-modal">
            <h3 style={{ marginTop: 0 }}>Esempio compilato — {currentMeta.label}</h3>
            <p className="muted" style={{ fontSize: ".85rem" }}>{currentMeta.desc}</p>
            <div style={{ fontSize: ".78rem", marginBottom: 8 }}>
              <strong>Colonne obbligatorie:</strong> {currentMeta.required.join(", ")}
              {currentHeaders.length > currentMeta.required.length && (
                <> · <strong>Opzionali:</strong> {currentHeaders.filter(h => !currentMeta.required.includes(h)).join(", ") || "—"}</>
              )}
            </div>
            <div className="ds-table-wrap">
              <table className="ds-table" style={{ fontSize: ".78rem" }}>
                <thead><tr>{currentHeaders.map(h => <th key={h}>{h}{currentMeta.required.includes(h) ? " *" : ""}</th>)}</tr></thead>
                <tbody>
                  {(currentMeta.example || []).map((row, i) => (
                    <tr key={i}>{currentHeaders.map(h => <td key={h}>{row[h] ?? ""}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <Button onClick={downloadExample} data-testid="import-example-download">Scarica esempio</Button>
              <Button variant="primary" onClick={() => setShowExample(false)}>Chiudi</Button>
            </div>
          </div>
        </div>
      )}
      {/* Modal conferma import */}
      {showConfirm && counts && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", display: "flex",
                      alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 8, padding: 20, maxWidth: 500, width: "100%" }}
               data-testid="import-confirm-modal">
            <h3 style={{ marginTop: 0 }}>Conferma importazione</h3>
            <p>Stai per importare <strong>{(counts.rows_read || 0) - (counts.errors || 0) - (counts.skipped || 0)}</strong> righe.</p>
            <p style={{ fontSize: ".85rem" }}>
              {counts.updated || 0} record saranno aggiornati e {counts.created || 0} saranno creati.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button onClick={() => setShowConfirm(false)}>Annulla</Button>
              <Button variant="primary" onClick={doApply} loading={busy} disabled={busy} data-testid="import-confirm-btn">Conferma importazione</Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}
